import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { Code2, ChevronDown, Copy, CheckCircle2 } from 'lucide-react';

/**
 * RequestViewer — Muestra la llamada HTTP en tres formatos: cURL | JavaScript | HTTP raw
 *
 * Props — request:
 * {
 *   method      : 'GET' | 'POST' | 'DELETE'
 *   baseUrl     : string
 *   queryParams : string | null
 *   headers     : object               SIN Authorization (se agrega automáticamente)
 *   formFields  : null | Array<{       para multipart/form-data
 *     name, type ('file'|'text'), fileName?, varName?, value?
 *   }>
 *   jsonBody    : object | null        para application/json
 *   jsPrefix    : string | null        código JS a mostrar antes del fetch (ej. conversión base64)
 * }
 */
const RequestViewer = ({ request }) => {
  const [open, setOpen]           = useState(false);
  const [activeTab, setActiveTab] = useState('curl');
  const [copied, setCopied]       = useState(false);

  if (!request) return null;

  const { method, baseUrl, queryParams, headers, formFields, jsonBody, jsPrefix } = request;
  const fullUrl    = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
  const allHeaders = { 'Authorization': 'Bearer {jwt_de_tu_sesion}', ...headers };
  const hasJson    = !!jsonBody;
  const hasForms   = !hasJson && formFields && formFields.length > 0;

  let host = '';
  try { host = new URL(baseUrl).host; } catch {}

  // ── Generadores de código ────────────────────────────────

  const getCurlCode = () => {
    const jsonHeaders = hasJson ? { ...allHeaders, 'Content-Type': 'application/json' } : allHeaders;
    const lines = [`curl -X ${method} \\`, `  "${fullUrl}" \\`];
    const keys = Object.keys(jsonHeaders);
    keys.forEach((k, i) => {
      const trailer = (i < keys.length - 1 || hasJson || hasForms) ? ' \\' : '';
      lines.push(`  -H "${k}: ${jsonHeaders[k]}"${trailer}`);
    });
    if (hasJson) {
      const jsonStr = JSON.stringify(jsonBody, null, 2).replace(/\n/g, '\n  ');
      lines.push(`  -d '${jsonStr}'`);
    } else if (hasForms) {
      formFields.forEach((f, i) => {
        const isLast = i === formFields.length - 1;
        if (f.type === 'file') {
          lines.push(`  -F "${f.name}=@/ruta/${f.fileName}"${isLast ? '' : ' \\'}`);
        } else {
          lines.push(`  -F "${f.name}=${f.value}"${isLast ? '' : ' \\'}`);
        }
      });
    }
    return lines.join('\n');
  };

  const getJsCode = () => {
    const lines = [];

    if (jsPrefix) {
      lines.push(jsPrefix);
      lines.push('');
    }

    if (hasForms) {
      lines.push('const formData = new FormData();');
      formFields.forEach(f => {
        if (f.type === 'file') {
          lines.push(`formData.append("${f.name}", ${f.varName}); // File (.${f.fileName?.split('.').pop() || 'bin'})`);
        } else {
          lines.push(`formData.append("${f.name}", "${f.value}");`);
        }
      });
      lines.push('');
    }

    lines.push('const response = await fetch(');
    lines.push(`  "${fullUrl}",`);
    lines.push('  {');
    lines.push(`    method: "${method}",`);
    lines.push('    headers: {');
    const fetchHeaders = hasJson
      ? { ...allHeaders, 'Content-Type': 'application/json' }
      : allHeaders;
    Object.entries(fetchHeaders).forEach(([k, v]) => {
      lines.push(`      "${k}": "${v}",`);
    });
    lines.push('    },');
    if (hasJson) {
      const bodyStr = JSON.stringify(jsonBody, null, 6)
        .replace(/\n/g, '\n    ');
      lines.push(`    body: JSON.stringify(${bodyStr}),`);
    } else if (hasForms) {
      lines.push('    body: formData,');
    }
    lines.push('  }');
    lines.push(');');
    lines.push('');
    lines.push('const data = await response.json();');
    if (!hasJson) {
      lines.push('console.log(data.comprobantes); // array de CFDIs');
    } else {
      lines.push('console.log(data);');
    }
    return lines.join('\n');
  };

  const getHttpCode = () => {
    let path = fullUrl;
    try { const u = new URL(fullUrl); path = u.pathname + (u.search || ''); } catch {}
    const lines = [`${method} ${path} HTTP/2`, `Host: ${host}`];
    const httpHeaders = hasJson
      ? { ...allHeaders, 'Content-Type': 'application/json' }
      : allHeaders;
    Object.entries(httpHeaders).forEach(([k, v]) => lines.push(`${k}: ${v}`));
    if (hasJson) {
      const jsonStr = JSON.stringify(jsonBody, null, 2);
      lines.push(`Content-Length: ${jsonStr.length}`);
      lines.push('');
      lines.push(jsonStr);
    } else if (hasForms) {
      lines.push('Content-Type: multipart/form-data; boundary=----boundary');
      lines.push('');
      formFields.forEach(f => {
        lines.push('------boundary');
        if (f.type === 'file') {
          lines.push(`Content-Disposition: form-data; name="${f.name}"; filename="${f.fileName}"`);
          lines.push('Content-Type: application/octet-stream');
          lines.push('');
          lines.push('[contenido binario del archivo]');
        } else {
          lines.push(`Content-Disposition: form-data; name="${f.name}"`);
          lines.push('');
          lines.push(f.value);
        }
      });
      lines.push('------boundary--');
    }
    return lines.join('\n');
  };

  const codeByTab = { curl: getCurlCode, js: getJsCode, http: getHttpCode };
  const getCode   = () => (codeByTab[activeTab] || getCurlCode)();

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(getCode()); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabLabels = { curl: 'cURL', js: 'JavaScript', http: 'HTTP' };

  return (
    <div className="request-viewer">
      <button className="request-viewer-toggle" onClick={() => setOpen(o => !o)}>
        <span className="request-viewer-toggle-left">
          <Code2 size={16} />
          Ver implementación de esta llamada
        </span>
        <ChevronDown size={15} className={`request-toggle-chevron ${open ? 'rotated' : ''}`} />
      </button>

      <Collapse in={open}>
        <div>
          <div className="request-viewer-body">

            <div className="request-url-bar">
              <span className={`method-badge method-${method.toLowerCase()}`}>
                {method}
              </span>
              <code className="request-url">{fullUrl}</code>
            </div>

            <div className="request-code-panel">
              <div className="request-code-header">
                <div className="request-code-tabs">
                  {Object.keys(tabLabels).map(tab => (
                    <button key={tab}
                      className={`request-code-tab ${activeTab === tab ? 'active' : ''}`}
                      onClick={() => { setActiveTab(tab); setCopied(false); }}>
                      {tabLabels[tab]}
                    </button>
                  ))}
                </div>
                <button className="request-copy-btn" onClick={handleCopy}>
                  {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
              <pre className="request-code-block">
                <code>{getCode()}</code>
              </pre>
            </div>

          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default RequestViewer;
